/**
 * Performance Optimizer Utility
 * 
 * Provides utilities for optimizing app performance including:
 * - Image preloading and optimization
 * - Resource prioritization
 * - Lazy loading coordination
 * - Cache warming strategies
 */

import { logger } from './logger';
import { contentCache } from './contentCache';

/**
 * Preload critical images
 * 
 * @param {Array<string>} imageUrls - Array of image URLs to preload
 * @param {Object} options - Preload options
 * @returns {Promise<void>}
 */
export const preloadImages = async (imageUrls, options = {}) => {
  const { priority = 'low', cache = true } = options;
  
  const preloadPromises = imageUrls.map(url => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = async () => {
        if (cache) {
          await contentCache.cacheImage(url);
        }
        resolve(url);
      };
      
      img.onerror = () => {
        logger.warn(`Failed to preload image: ${url}`);
        resolve(null); // Don't reject, just log
      };
      
      // Set priority hint if supported
      if ('fetchPriority' in img) {
        img.fetchPriority = priority;
      }
      
      img.src = url;
    });
  });
  
  await Promise.allSettled(preloadPromises);
  logger.debug(`Preloaded ${imageUrls.length} images`);
};

/**
 * Preload critical resources (study guides, questions)
 * 
 * @param {Array<string>} resources - Array of resource keys to preload
 * @returns {Promise<void>}
 */
export const preloadResources = async (resources) => {
  try {
    await contentCache.init();
    
    const preloadPromises = resources.map(async (resource) => {
      try {
        // Check if already cached
        const cached = await contentCache.getCachedContent(resource.key, resource.type);
        if (cached) {
          return cached;
        }
        
        // Cache the resource
        if (resource.data) {
          await contentCache.cacheContent(resource.key, resource.data, resource.type);
        }
      } catch (error) {
        logger.warn(`Failed to preload resource ${resource.key}:`, error);
      }
    });
    
    await Promise.allSettled(preloadPromises);
    logger.debug(`Preloaded ${resources.length} resources`);
  } catch (error) {
    logger.error('Failed to preload resources:', error);
  }
};

/**
 * Warm up cache with frequently accessed content
 * 
 * @param {Object} content - Content to warm up
 * @returns {Promise<void>}
 */
export const warmCache = async (content = {}) => {
  try {
    await contentCache.init();
    
    const warmPromises = [];
    
    // Warm up study guides
    if (content.studyGuides) {
      Object.entries(content.studyGuides).forEach(([key, guide]) => {
        warmPromises.push(
          contentCache.cacheContent(`study-guide-${key}`, guide, 'study_guide')
        );
      });
    }
    
    // Warm up questions (cache first 10 of each subject)
    if (content.questions) {
      Object.entries(content.questions).forEach(([subject, questions]) => {
        const sampleQuestions = questions.slice(0, 10);
        warmPromises.push(
          contentCache.cacheContent(`questions-${subject}`, sampleQuestions, 'questions')
        );
      });
    }
    
    await Promise.allSettled(warmPromises);
    logger.debug('Cache warmed up successfully');
  } catch (error) {
    logger.error('Failed to warm cache:', error);
  }
};

/**
 * Optimize image loading with priority hints
 * 
 * @param {string} url - Image URL
 * @param {Object} options - Loading options
 * @returns {Promise<HTMLImageElement>}
 */
export const loadImageWithPriority = (url, options = {}) => {
  const { priority = 'auto', cache = true } = options;
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    // Set priority hint
    if ('fetchPriority' in img) {
      img.fetchPriority = priority;
    }
    
    // Set loading attribute
    if (priority === 'high') {
      img.loading = 'eager';
    } else {
      img.loading = 'lazy';
    }
    
    img.onload = async () => {
      if (cache) {
        await contentCache.cacheImage(url);
      }
      resolve(img);
    };
    
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };
    
    img.src = url;
  });
};

/**
 * Debounce function for performance optimization
 * 
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for performance optimization
 * 
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Request idle time for non-critical work
 * 
 * @param {Function} callback - Callback to run during idle time
 * @param {Object} options - Options
 */
export const requestIdleCallback = (callback, options = {}) => {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, options);
  } else {
    // Fallback for browsers without requestIdleCallback
    return setTimeout(callback, options.timeout || 1);
  }
};

/**
 * Cancel idle callback
 * 
 * @param {number} id - Callback ID
 */
export const cancelIdleCallback = (id) => {
  if ('cancelIdleCallback' in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
};

/**
 * Prefetch resources for faster navigation
 * 
 * @param {Array<string>} urls - URLs to prefetch
 */
export const prefetchResources = (urls) => {
  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  });
};

/**
 * Preconnect to external domains
 * 
 * @param {Array<string>} domains - Domains to preconnect to
 */
export const preconnectDomains = (domains) => {
  domains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    document.head.appendChild(link);
  });
};

export default {
  preloadImages,
  preloadResources,
  warmCache,
  loadImageWithPriority,
  debounce,
  throttle,
  requestIdleCallback,
  cancelIdleCallback,
  prefetchResources,
  preconnectDomains
};

